import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';

import { DatePicker } from '@vaadin/vaadin-date-picker';
import { TimePicker } from '@vaadin/vaadin-time-picker';
import format from 'date-fns/format';
import { removeInertFromVaadinOverlay } from '../../helpers/inert';

@customElement('date-time-range-picker')
export class DateTimeRangePickerComponent extends LitElement {
    static amplitude_callsite: string = 'Date Time Range Picker';

    @property({ type: Boolean })
    clearEnabled: boolean = false;

    @property({ type: Boolean })
    filterPast: boolean = false;

    @query('#startDate')
    public startDateElement!: DatePicker;

    @query('#endDate')
    public endDateElement!: DatePicker;

    @query('#startTime')
    public startTimeElement!: TimePicker;

    @query('#endTime')
    public endTimeElement!: TimePicker;

    @property({ type: Boolean })
    timeOnly: boolean = false;

    @property({ type: Boolean })
    dateOnly: boolean = false;

    @property({ type: Boolean })
    showEndDateTime: boolean = true;

    @property({ type: Boolean })
    isFilterView: boolean = false;

    @property({ type: String })
    customHeader?: string;

    @state()
    allDay: boolean = true;

    @state()
    isIncludingEndDateTime: boolean = true;

    @state()
    timezoneShorthand: string = '';

    @state()
    hideInputs: boolean = false;

    @state()
    filterType: string = '';

    @state()
    hideAllDayToggle: boolean = false;

    @state()
    showEndDateTimeToggle: boolean = true;

    firstUpdated(_changedProperties: any) {
        this.removeInertOnDropdown();

        if (this.isFilterView) {
            this.toggleNow();
        }
    }

    updated(_changedProperties: any) {
        if (_changedProperties.has('filterPast') === true) {
            if (this.filterPast) {
                this.startDateElement.value = format(new Date(), 'yyyy-MM-dd');
                this.startTimeElement.value = format(new Date(), 'HH:mm');
            }
        }
    }

    private removeInertOnDropdown() {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        removeInertFromVaadinOverlay('VAADIN-DATE-PICKER-OVERLAY');
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        removeInertFromVaadinOverlay('VAADIN-TIME-PICKER-OVERLAY');
    }

    private toggleIncludeTime(e: any) {
        this.allDay = e.detail;
        if (this.allDay) {
            this.startTimeElement.value = '';
            this.endTimeElement.value = '';
        }
        // Notify the host so consumers (like the live time-range
        // preview) can react. Without this, flipping all-day silently
        // changes the time fields without telling anyone.
        this.dispatchEvent(new CustomEvent('change', {
            bubbles: true,
            composed: true,
            detail: { allDay: this.allDay },
        }));
    }

    private toggleEndDateTime(e: any) {
        this.isIncludingEndDateTime = e.detail;
        if (!this.isIncludingEndDateTime) {
            this.clearEndDateTime();
        }
        // Toggling end-date visibility clears the end fields and
        // changes the meaning of the row — broadcast so listeners
        // re-derive their derived state.
        this.dispatchEvent(new CustomEvent('change', {
            bubbles: true,
            composed: true,
            detail: { isIncludingEndDateTime: this.isIncludingEndDateTime },
        }));
    }

    private clearStartDateTime() {
        this.startDateElement.value = '';
        this.startTimeElement.value = '';
    }

    private clearEndDateTime() {
        this.endDateElement.value = '';
        this.endTimeElement.value = '';
    }

    public clearAll() {
        this.startDateElement.value = this.filterPast ? format(new Date(), 'yyyy-MM-dd') : '';
        this.startTimeElement.value = this.filterPast ? format(new Date(), 'HH:mm') : '';
        this.endDateElement.value = '';
        this.endTimeElement.value = '';
        this.isIncludingEndDateTime = true;
    }

    public toggleNow() {
        // get now through next hour
        this.startDateElement.value = format(new Date(), 'yyyy-MM-dd');
        this.startTimeElement.value = format(new Date(), 'HH:mm');
        this.endDateElement.value = format(new Date(), 'yyyy-MM-dd');
        this.endTimeElement.value = format(new Date(new Date().getTime() + 60 * 60 * 1000), 'HH:mm');
        this.isIncludingEndDateTime = false;
        this.hideInputs = true;
        this.filterType = 'now';
    }

    public toggleToday() {
        this.startDateElement.value = format(new Date(), 'yyyy-MM-dd');
        this.startTimeElement.value = '00:00';
        this.endDateElement.value = format(new Date(), 'yyyy-MM-dd');
        this.endTimeElement.value = '23:59';
        this.isIncludingEndDateTime = true;
        this.hideInputs = true;
        this.filterType = 'today';
    }

    public toggleTomorrow() {
        const tomorrow = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
        this.startDateElement.value = format(tomorrow, 'yyyy-MM-dd');
        this.startTimeElement.value = '00:00';
        this.endDateElement.value = format(tomorrow, 'yyyy-MM-dd');
        this.endTimeElement.value = '23:59';
        this.isIncludingEndDateTime = true;
        this.hideInputs = true;
        this.filterType = 'tomorrow';
    }

    public toggleThisWeek() {
        const today = new Date();
        const day = today.getDay();
        const diff = today.getDate() - day + (day === 0 ? -6 : 1);
        const startOfWeek = new Date(today.setDate(diff));
        const endOfWeek = new Date(today.setDate(startOfWeek.getDate() + 6));
        this.startDateElement.value = format(startOfWeek, 'yyyy-MM-dd');
        this.startTimeElement.value = '00:00';
        this.endDateElement.value = format(endOfWeek, 'yyyy-MM-dd');
        this.endTimeElement.value = '23:59';
        this.isIncludingEndDateTime = true;
        this.hideInputs = true;
        this.filterType = 'thisWeek';
    }

    public toggleThisWeekend() {
        const today = new Date();
        const day = today.getDay();
        const diff = today.getDate() - day + (day === 0 ? -6 : 1);
        const startOfWeek = new Date(today.setDate(diff));
        const endOfWeek = new Date(today.setDate(startOfWeek.getDate() + 6));
        const startOfWeekend = new Date(endOfWeek.setDate(endOfWeek.getDate() - 1));
        const endOfWeekend = new Date(endOfWeek.setDate(endOfWeek.getDate() + 1));
        this.startDateElement.value = format(startOfWeekend, 'yyyy-MM-dd');
        this.startTimeElement.value = '00:00';
        this.endDateElement.value = format(endOfWeekend, 'yyyy-MM-dd');
        this.endTimeElement.value = '23:59';
        this.isIncludingEndDateTime = true;
        this.hideInputs = true;
        this.filterType = 'thisWeekend';
    }

    public toggleOnSpecificDates() {
        this.startDateElement.value = format(new Date(), 'yyyy-MM-dd');
        this.startTimeElement.value = '00:00';
        this.endDateElement.value = format(new Date(), 'yyyy-MM-dd');
        this.endTimeElement.value = '23:59';
        this.isIncludingEndDateTime = true;
        this.hideInputs = false;
        this.filterType = 'specificDates';
    }

    public toggleAtSpecificTime() {
        // this should ONLY run when has_times_only is on because of the dummy date situation. other points with a dummy date will be converted to today
        this.startDateElement.value = format(new Date(), 'yyyy-MM-dd');
        this.startTimeElement.value = '08:00';
        this.endDateElement.value = format(new Date(), 'yyyy-MM-dd');
        this.endTimeElement.value = '12:00';
        this.isIncludingEndDateTime = true;
        this.hideInputs = false;
        this.allDay = false;
        this.isIncludingEndDateTime = true;
        this.filterType = 'specificTimes';
    }

    render() {
        const header = this.customHeader ? this.customHeader :this.timeOnly ? 'Time' : this.dateOnly ? 'Date' : !this.allDay ? 'Date and Time' : 'Date';
        return html`
            <div class="pickerContainer ${this.hideInputs ? 'hidden' : ''}">
                <h5 class="header">${header} ${this.timezoneShorthand && this.timezoneShorthand !== '' ? `(${this.timezoneShorthand})` : ''}</h5>
                <div class="subFieldLabel">
                    ${this.isIncludingEndDateTime && this.showEndDateTime ? 'Start ' : ''}
                </div>
                <div class="flexContainer" style="width:90%;">
                    <vaadin-date-picker
                        id="startDate"
                        class="input ${!this.allDay && !this.dateOnly ? 'half-width' :''}"
                        placeholder="${this.isIncludingEndDateTime && this.showEndDateTime ? 'Start ' : ''}"
                        ?hidden=${this.timeOnly}
                        @change="${(e: CustomEvent) => {
                            this.dispatchEvent(new CustomEvent('change', {
                                bubbles: true,
                                composed: true,
                                detail: e.detail
                            }));
                        }}"
                    ></vaadin-date-picker>
                    <vaadin-time-picker
                        id="startTime"
                        class="${!this.timeOnly ? 'half-width' :''}"
                        ?hidden=${(this.dateOnly || this.allDay) && !this.timeOnly}
                        @change="${(e: CustomEvent) => {
                            this.dispatchEvent(new CustomEvent('change', {
                                bubbles: true,
                                composed: true,
                                detail: e.detail
                            }));
                        }}"
                    ></vaadin-time-picker>
                    ${this.clearEnabled
                        ? html`
                              <mwc-icon class="clearIcon" @click=${this.clearStartDateTime}
                                  >close</mwc-icon
                              >
                          `
                        : ``}
                </div>
                <div class="subFieldLabel" ?hidden=${!this.isIncludingEndDateTime}>
                    ${this.isIncludingEndDateTime && this.showEndDateTime ? 'End ' : ''}
                </div>
                <div class="flexContainer" style="width:90%">
                    <vaadin-date-picker
                        id="endDate"
                        class="input ${!this.allDay && !this.dateOnly ? 'half-width' :''}"
                        placeholder="${this.isIncludingEndDateTime && this.showEndDateTime ? 'End ' : ''}"
                        ?hidden=${!this.isIncludingEndDateTime || !this.showEndDateTime || this.timeOnly}
                        @change="${(e: CustomEvent) => {
                            this.dispatchEvent(new CustomEvent('change', {
                                bubbles: true,
                                composed: true,
                                detail: e.detail
                            }));
                        }}"
                    ></vaadin-date-picker>
                    <vaadin-time-picker
                        id="endTime"
                        class="${!this.timeOnly ? 'half-width' :''}"
                        ?hidden=${this.dateOnly || this.allDay || !this.isIncludingEndDateTime || !this.showEndDateTime}
                        @change="${(e: CustomEvent) => {
                            this.dispatchEvent(new CustomEvent('change', {
                                bubbles: true,
                                composed: true,
                                detail: e.detail
                            }));
                        }}"
                    ></vaadin-time-picker>
                    ${this.clearEnabled && this.isIncludingEndDateTime
                        ? html`
                              <mwc-icon class="clearIcon" @click=${this.clearEndDateTime}
                                  >close</mwc-icon
                              >
                          `
                        : ``}
                </div>
                <div class="flexContainer switchContainer">
                    ${this.showEndDateTime && !this.isFilterView && this.showEndDateTimeToggle ? html`
                        <div class="flexContainer">
                            <form-switch
                                id="toggleEndDate"
                                name="toggleEndDate"
                                ?checked=${this.isIncludingEndDateTime}
                                @change="${this.toggleEndDateTime}"
                            >
                                <translated-span>Show End ${this.timeOnly ? 'Time' : this.dateOnly ? 'Date' : !this.allDay ? 'Date and Time' : 'Date'}</translated-span>
                            </form-switch>
                            <form-tooltip
                                tipwidth="220px"
                                leftPadding="-100%"
                                position="top"
                                message="If this toggle is turned off, we will set the end date equal to the start date. If this toggle is turned in and no end date is provided, the event will continue permanently into the future."
                            >
                                <div class="optionItem flexContainer alignCenter">
                                    <mwc-icon>info_outline</mwc-icon>
                                </div>
                            </form-tooltip>
                        </div>
                    ` : ''}
                    ${this.dateOnly || this.timeOnly || this.hideAllDayToggle ? '' : html`
                        <div class="flexContainer">
                            <form-switch
                                id="includeTimeToggler"
                                name="includeTimeToggler"
                                ?checked=${this.allDay || this.timeOnly}
                                @change="${this.toggleIncludeTime}"
                            >
                                <translated-span>All Day</translated-span>
                            </form-switch>
                            <form-tooltip
                                tipwidth="220px"
                                leftPadding="-100%"
                                position="top"
                                message="If this toggle is turned off, we will set the start time to 12:00AM and the end time (if end date is provided) to 11:59PM."
                            >
                                <div class="optionItem flexContainer alignCenter">
                                    <mwc-icon>info_outline</mwc-icon>
                                </div>
                            </form-tooltip>
                        </div>
                    `}
                </div>
            </div>
        `;
    }

    static get styles() {
        return css`

            .flexContainer {
                display: flex;
                align-items: center;
                position: relative;
            }

            .hidden {
                display: none;
            }

            .header {
                font-weight: 500;
                font-size: 0.8rem;
                margin: auto 10px 5px 0px;
                white-space: nowrap;
            }

            .subFieldLabel {
                color: var(--theme-input-color);
                font-weight: 300;
                margin-top: 0.5rem;
            }

            .half-width {
                width: 48%;
            }

            vaadin-date-picker {
                flex: 1;
                font-size: 1rem;
                color: var(--theme-input-color);
            }

            vaadin-time-picker {
                font-size: 1rem;
                padding-left: 4px;
            }

            form-switch {
                font-size: 1rem;
            }

            form-tooltip {
                margin-left: 0.25rem;
            }

            .switchContainer {
                margin-top: 1rem;
            }

            .switchContainer .flexContainer {
                flex: 1;
            }

            .clearIcon {
                position: absolute;
                right: -2rem;
                cursor: pointer;
                color: var(--main-invalid-color);
            }
        `;
    }
}
